import type {
  SourceAutoPopulationStatus,
  SourceClassificationMethod,
  SourceDocumentRecord,
  SourceFieldMapping,
  SupportedDocumentType,
} from '@/features/case-workspace/types'
import type { Case, EntryField, EntryLine, IntakeDocument, IntakeDocumentExtraction } from '@/lib/cases'

type ClassificationResult = {
  documentType: SupportedDocumentType | null
  label: string
  method: SourceClassificationMethod
}

export const SOURCE_FIELD_MAPPINGS: Record<SupportedDocumentType, SourceFieldMapping[]> = {
  cbp_7501: [
    { sourceFieldKey: 'importer_of_record', targetFieldKey: 'importer_of_record' },
    { sourceFieldKey: 'port_of_entry', targetFieldKey: 'port_of_entry' },
    { sourceFieldKey: 'entry_type', targetFieldKey: 'entry_type' },
    { sourceFieldKey: 'entered_value_usd', targetFieldKey: 'total_entered_value' },
    { sourceFieldKey: 'estimated_duty_usd', targetFieldKey: 'duty_total' },
  ],
  manifest: [
    { sourceFieldKey: 'manifest_number', targetFieldKey: 'manifest_number' },
    { sourceFieldKey: 'bill_of_lading', targetFieldKey: 'uploaded_file_reference' },
    { sourceFieldKey: 'mode_of_transport', targetFieldKey: 'mode_of_transport' },
    { sourceFieldKey: 'estimated_arrival', targetFieldKey: 'arrival_date' },
    { sourceFieldKey: 'vessel_name', targetFieldKey: 'vessel_name' },
    { sourceFieldKey: 'carrier_name', targetFieldKey: 'carrier_name' },
    { sourceFieldKey: 'country_of_origin', targetFieldKey: 'last_foreign_port_before_us' },
  ],
  commercial_invoice: [
    { sourceFieldKey: 'consignee', targetFieldKey: 'consignee_name' },
    { sourceFieldKey: 'importer_of_record', targetFieldKey: 'importer_of_record_name' },
    { sourceFieldKey: 'entered_value_usd', targetFieldKey: 'total_invoice_value' },
    { sourceFieldKey: 'bill_of_lading', targetFieldKey: 'bill_of_lading_number' },
    { sourceFieldKey: 'port_of_entry', targetFieldKey: 'port_of_entry_destination' },
    { sourceFieldKey: 'country_of_origin', targetFieldKey: 'country_of_origin' },
  ],
  permit_license: [
    { sourceFieldKey: 'permit_number', targetFieldKey: 'permit_number' },
    { sourceFieldKey: 'agency_name', targetFieldKey: 'agency_name' },
    { sourceFieldKey: 'commodity_description', targetFieldKey: 'covered_commodity' },
  ],
}

export function classifySourceDocument(doc: IntakeDocument): ClassificationResult {
  const lower = doc.original_filename.toLowerCase()
  const ocrText = (doc.ocr_text ?? '').toLowerCase()

  if (lower.includes('7501') || ocrText.includes('cbp form 7501') || ocrText.includes('entry summary')) {
    return {
      documentType: 'cbp_7501',
      label: 'CBP Form 7501 source',
      method: lower.includes('7501') ? 'filename' : 'ocr_text',
    }
  }
  if (lower.includes('manifest') || ocrText.includes('manifest') || ocrText.includes('bill of lading')) {
    return {
      documentType: 'manifest',
      label: 'Manifest source',
      method: lower.includes('manifest') ? 'filename' : 'ocr_text',
    }
  }
  if (lower.includes('invoice') || ocrText.includes('commercial invoice') || ocrText.includes('invoice number')) {
    return {
      documentType: 'commercial_invoice',
      label: 'Commercial invoice source',
      method: lower.includes('invoice') ? 'filename' : 'ocr_text',
    }
  }
  if (lower.includes('permit') || lower.includes('license') || ocrText.includes('permit') || ocrText.includes('license')) {
    return {
      documentType: 'permit_license',
      label: 'Permit / License source',
      method: lower.includes('permit') || lower.includes('license') ? 'filename' : 'ocr_text',
    }
  }

  return {
    documentType: null,
    label: 'Uploaded case document',
    method: 'manual_review',
  }
}

export function labelForDocumentType(documentType: SupportedDocumentType | null) {
  if (documentType === 'cbp_7501') return 'CBP Form 7501 source'
  if (documentType === 'manifest') return 'Manifest source'
  if (documentType === 'commercial_invoice') return 'Commercial invoice source'
  if (documentType === 'permit_license') return 'Permit / License source'
  return 'Uploaded case document'
}

export function getSourceValue(params: {
  kase: Case
  fields: EntryField[]
  lines: EntryLine[]
  extraction?: IntakeDocumentExtraction
  sourceFieldKey: string
}) {
  const { kase, fields, lines, extraction, sourceFieldKey } = params

  const extractedFieldValue = extraction?.extracted_fields?.[sourceFieldKey]
  if (extractedFieldValue != null && extractedFieldValue !== '') {
    return String(extractedFieldValue)
  }

  const fieldMatch = fields.find((field) => field.field_key === sourceFieldKey)?.field_value
  if (fieldMatch != null && fieldMatch !== '') {
    return fieldMatch
  }

  switch (sourceFieldKey) {
    case 'importer_of_record':
      return kase.importer_of_record ?? ''
    case 'port_of_entry':
      return kase.port_of_entry ?? ''
    case 'entered_value_usd':
      return kase.total_value_usd == null ? '' : String(kase.total_value_usd)
    case 'estimated_duty_usd':
      return kase.total_duty_usd == null ? '' : String(kase.total_duty_usd)
    case 'commodity_description':
      return lines[0]?.description ?? ''
    case 'vessel_name':
      return fields.find((field) => field.field_key === 'vessel_name')?.field_value ?? ''
    case 'carrier_name':
      return fields.find((field) => field.field_key === 'carrier_name')?.field_value ?? ''
    default:
      return ''
  }
}

export function resolveSourceAutoPopulationStatus(params: {
  extractionStatus: SourceDocumentRecord['extractionStatus']
  mirrorsDocumentType: SupportedDocumentType | null
  autoPopulatedFieldCount: number
  conflictFieldCount: number
}): SourceAutoPopulationStatus {
  const { extractionStatus, mirrorsDocumentType, autoPopulatedFieldCount, conflictFieldCount } = params
  if (!mirrorsDocumentType || extractionStatus !== 'done') {
    return extractionStatus === 'error' ? 'needs_review' : extractionStatus === 'done' ? 'not_started' : 'pending'
  }
  if (conflictFieldCount > 0) return 'conflict'
  if (autoPopulatedFieldCount > 0) return 'applied'
  return 'not_started'
}
